import type { ButtonHTMLAttributes, ReactNode } from "react";
import { cloneElement, isValidElement, useState } from "react";
import type { ButtonProps as MuiButtonProps } from "@mui/material/Button";

import * as S from "./styles";
import type { ButtonAppearance, ButtonSize } from "./variants";

type ButtonProps = {
  children: ReactNode;
  appearance?: ButtonAppearance;
  size?: ButtonSize;
  icon?: ReactNode;
  loading?: boolean;
  onClick?: ButtonHTMLAttributes<HTMLButtonElement>["onClick"];
  disabled?: boolean;
  type?: ButtonHTMLAttributes<HTMLButtonElement>["type"];
  "data-testid"?: string;
  variant?: MuiButtonProps["variant"];
  color?: string;
  backgroundColor?: string;
  padding?: string;
  width?: string;
  height?: string;
  border?: string;
  borderRadius?: string;
  hoverColor?: string;
  hoverBackgroundColor?: string;
  alignSelf?: string;
  fontSize?: string;
  minWidth?: string;
  disabledBackgroundColor?: string;
  disabledOpacity?: number;
} & Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "type" | "color" | "children" | "onClick"
>;

const Button = ({
  appearance,
  size = "default",
  icon,
  loading = false,
  children,
  onClick,
  disabled = false,
  type = "button",
  "data-testid": testId,
  variant,
  color = "white",
  backgroundColor = "#01AB7D",
  padding = "0px",
  width = "fit-content",
  height = "auto",
  border = "none",
  borderRadius = "0",
  hoverColor = color,
  hoverBackgroundColor = backgroundColor,
  alignSelf = "flex-start",
  fontSize,
  minWidth,
  disabledBackgroundColor,
  disabledOpacity,
  ...rest
}: ButtonProps) => {
  const [isHovered, setIsHovered] = useState(false);

  if (appearance) {
    return (
      <S.VariantButton
        type={type}
        onClick={onClick}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        $appearance={appearance}
        $size={size}
        data-testid={testId}
        data-appearance={appearance}
        data-size={size}
        data-loading={loading ? "true" : undefined}
        {...rest}
      >
        {loading ? (
          <S.LoadingSpinner aria-hidden data-testid="button-loading-spinner" />
        ) : icon ? (
          <S.IconSlot>{icon}</S.IconSlot>
        ) : null}
        {children}
      </S.VariantButton>
    );
  }

  const getChildrenWithProps = () => {
    if (isValidElement<{ isHovered?: boolean }>(children)) {
      return cloneElement(children, { isHovered });
    }

    return children;
  };

  return (
    <S.ButtonCustom
      variant={variant}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-testid={testId}
      $color={isHovered && hoverColor ? hoverColor : color}
      $backgroundColor={
        isHovered && hoverBackgroundColor ? hoverBackgroundColor : backgroundColor
      }
      $padding={padding}
      $width={width}
      $height={height}
      $border={border}
      $borderRadius={borderRadius}
      $alignSelf={alignSelf}
      $fontSize={fontSize}
      $minWidth={minWidth}
      $disabledBackgroundColor={disabledBackgroundColor}
      $disabledOpacity={disabledOpacity}
      {...rest}
    >
      {loading && (
        <S.LoadingSpinner aria-hidden data-testid="button-loading-spinner" />
      )}
      {getChildrenWithProps()}
    </S.ButtonCustom>
  );
};
export default Button;
